import { Button, Radio, Tooltip } from "antd";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { API_USERDATA_MAILSIGNATUREOPTION } from "General/APIEnums";
import { RichEditor, RichEditorInstance } from "Pages/Project/Overview/Components/RichEditor/RichEditor";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface EmailSignatureFormProps extends ModalComponentProps {
  initialData: Pick<API_UserSettings, "mailSignature" | "mailSignatureOption">;
  onSubmit: (values: Pick<API_UserSettings, "mailSignature" | "mailSignatureOption">) => Promise<void>;
}

export const EmailSignatureForm = ({ initialData, onSubmit, hideModal }: EmailSignatureFormProps) => {
  const { t } = useTranslation(I18N_NS.USER_SETTINGS);
  const [mailSignatureOption, setMailSignatureOption] = useState(initialData.mailSignatureOption);
  const editor = useRef<RichEditorInstance>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload: EmailSignatureFormProps["initialData"] = {
        mailSignatureOption: mailSignatureOption,
      };
      if (mailSignatureOption) {
        payload.mailSignature = editor.current?.getHtml() || null;
      }
      await onSubmit({
        mailSignature: editor.current?.getHtml(),
        mailSignatureOption: mailSignatureOption,
      });
      hideModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div>
        <Radio.Group onChange={(e) => setMailSignatureOption(e.target.value)} value={mailSignatureOption}>
          <Tooltip
            title={t(
              // eslint-disable-next-line max-len
              "No e-mail signature is added to messages you send with this linked user. Use this when you do not want a signature for this account."
            )}
          >
            <Radio value={API_USERDATA_MAILSIGNATUREOPTION.NONE}>{t("None")}</Radio>
          </Tooltip>
          <Tooltip
            title={t(
              // eslint-disable-next-line max-len
              "Messages sent with this linked user use the e-mail signature from your PAVE account settings. If you change the PAVE account signature, it applies to every linked user set to this option."
            )}
          >
            <Radio value={API_USERDATA_MAILSIGNATUREOPTION.UNIFIED}>{t("Unified")}</Radio>
          </Tooltip>
          <Tooltip
            title={t(
              // eslint-disable-next-line max-len
              "Messages sent with this linked user use the e-mail signature stored for that user. You can maintain it separately from your PAVE account signature."
            )}
          >
            <Radio value={API_USERDATA_MAILSIGNATUREOPTION.CUSTOM}>{t("Custom")}</Radio>
          </Tooltip>
        </Radio.Group>
      </div>
      {mailSignatureOption === API_USERDATA_MAILSIGNATUREOPTION.CUSTOM && (
        <div style={{ margin: "10px 0" }}>
          <RichEditor initialValue={initialData.mailSignature ? initialData.mailSignature : ""} ref={editor} />
        </div>
      )}
      <div className="text-right">
        <Button type="primary" onClick={handleSubmit} loading={loading}>
          {t("Save")}
        </Button>
      </div>
    </div>
  );
};
